import { Input, PhoneIcon } from '@cof/plastic-components';
import { useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import { withText } from '../../../text/textStore';
import ErrorHandler from './ErrorHandler';
import { autoCompleteMapping, FORM_SECTIONS } from './FormIDs';
import { useContext, useEffect, useState } from 'react';
import { FormUtilsContext } from '../QuickCheck';
import { getAriaDescribedBy } from '../../../utilities/utils';
import { useLandmarkRefsContext } from '../../../components/LandmarkRefsProvider';

const getMobileValidationRules = (homeNumber: any) => ({
    required: homeNumber ? false : 'At least one number required',
    maxLength: { value: 11, message: 'Mobile number must be 11 digits' },
    minLength: { value: 11, message: 'Mobile number must be 11 digits' },
    pattern: { value: /^07\d{9}$/, message: 'Mobile number must be a valid UK Mobile phone number' },
    setValueAs: (value: any) => value.replace(/\s+/g, '').replace(/^\+44/, '0')
});

const getHomeValidationRules = () => ({
    required: false,
    maxLength: { value: 11, message: 'Home number must be at most 11 digits' },
    minLength: { value: 10, message: 'Home number must be at least 10 digits' },
    pattern: { value: /^0[1,2]\d{8,9}$/, message: 'Home number must be a valid UK Home phone number' },
    setValueAs: (value: any) => value.replace(/\s+/g, '').replace(/^\+44/, '0')
});

const getValidationRules = (name: any, homeNumber: any) => {
    return name === 'mobileNumber' ? getMobileValidationRules(homeNumber) : getHomeValidationRules();
};

const getInputProps = (id: any, name: any, register: any, homeNumber: any) => {
    const isMobile = name === 'mobileNumber';
    return {
        id,
        ...register(id, getValidationRules(name, homeNumber)),
        'autoComplete': isMobile ? autoCompleteMapping['mobile-number'] : autoCompleteMapping['home-number'],
        'style': { width: '100%' },
        'aria-describedby': `${id}-error`
    };
};

const getExplanation = (id: any, Text: any) => {
    if (id !== FORM_SECTIONS.AboutYou.MobileNumber) return null;

    return {
        Icon: PhoneIcon,
        body: (
            <span id={`${id}-explanation-box-text`}>
                <Text path="Explanation" />
            </span>
        ),
        prefix: id
    };
};

const PhoneNumber = ({ id, name, Text }: any) => {
    const {
        register,
        formState: { touchedFields, errors },
        watch,
        trigger
    } = useFormContext();
    // @ts-expect-error TS2339: Property 'variantTernary' does not exist on type 'null'.
    const { variantTernary } = useContext(FormUtilsContext);
    // @ts-expect-error TS2339: Property 'submitAttempted' does not exist on type ''.
    const { submitAttempted } = useLandmarkRefsContext();
    const [hasFocused, setHasFocused] = useState(false);
    // @ts-expect-error TS2769: No overload matches this call.
    watch([FORM_SECTIONS.AboutYou.MobileNumber, FORM_SECTIONS.AboutYou.HomeNumber]);

    // @ts-expect-error TS2769: No overload matches this call.
    const homeNumber = watch(FORM_SECTIONS.AboutYou.HomeNumber);

    useEffect(() => {
        // @ts-expect-error TS2538: Type 'undefined' cannot be used as an index type.
        if (touchedFields[FORM_SECTIONS.AboutYou.MobileNumber]) {
            trigger(FORM_SECTIONS.AboutYou.MobileNumber);
        }
    }, [homeNumber, trigger, touchedFields]);

    const explanation = getExplanation(id, Text);
    const additionalIDs = hasFocused && explanation ? [`${id}-explanation-box-text`] : [];

    return (
        <>
            <Input
                {...getInputProps(id, name, register, homeNumber)}
                variant={variantTernary(id)}
                explanation={explanation}
                onFocus={() => setHasFocused(true)}
                // @ts-expect-error TS2345: Argument of type 'string[]' is not assignable to parameter of type 'never[]'.
                aria-describedby={getAriaDescribedBy(id, submitAttempted, additionalIDs, errors)}
                data-qa-id={id}
            />
            <ErrorHandler id={id} show={!!touchedFields[id]} />
        </>
    );
};
PhoneNumber.propTypes = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    Text: PropTypes.func.isRequired
};

export { PhoneNumber };
export default withText('AboutYou.MobileNumber')(PhoneNumber);
